import type { FileItem } from '../features/steps/stepFlow';
import { useT, type DictKey } from '../i18n';
import { CloseIcon } from './Icons';

const LABEL: Record<FileItem['status'], DictKey> = {
  waiting: 'upload.waiting',
  preparing: 'upload.preparing',
  uploading: 'upload.uploading',
  done: 'upload.done',
  failed: 'upload.failed',
};

/** One row per photo: name, status, progress bar; a waiting/failed photo can be removed. */
export function UploadProgress({ files, onRemove, locked }: { files: FileItem[]; onRemove?: (key: string) => void; locked?: boolean }) {
  const t = useT();
  if (!files.length) return null;
  const done = files.filter((f) => f.status === 'done').length;
  return (
    <div className="mt-3">
      <p className="mb-2 text-[14px] text-lab">{t('upload.progress', { done, total: files.length })}</p>
      <ul className="space-y-2">
        {files.map((f) => (
          <li key={f.key} className="rounded-xl border border-line px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-[15px]">{f.name}</span>
              <span className={`text-[13px] ${f.status === 'failed' ? 'text-bad' : f.status === 'done' ? 'text-ok' : 'text-lab'}`}>
                {t(LABEL[f.status])}
              </span>
              {onRemove && !locked && f.status !== 'done' ? (
                <button type="button" aria-label={t('step.removePhoto')} className="-mr-2 inline-flex min-h-tap min-w-tap items-center justify-center" onClick={() => onRemove(f.key)}>
                  <CloseIcon />
                </button>
              ) : null}
            </div>
            {f.status === 'uploading' || f.status === 'preparing' ? (
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-hov" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(f.progress * 100)}>
                <div className="h-full bg-fg transition-[width]" style={{ width: `${Math.max(4, Math.round(f.progress * 100))}%` }} />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
